<template>
  <div class="workspace-page journal journal-index">
    <header class="journal-heading">
      <div>
        <p class="workspace-eyebrow">
          {{ $t('journal.eyebrow') }}
        </p><h1>{{ $t('journal.title') }} <span>{{ $t('journal.title-accent') }}</span></h1><p>{{ $t('journal.description') }}</p>
      </div><router-link
        to="/artists-catalog"
        class="product-text-button"
      >
        {{ $t('journal.meet-community') }}<GrowthIcon name="arrow" />
      </router-link>
    </header>
    <nav
      class="journal-topics"
      :aria-label="$t('journal.browse-topics')"
    >
      <router-link
        :to="{ path: '/blog', query: query(1, '') }"
        :aria-current="!topic ? 'page' : undefined"
        :class="{ active: !topic }"
      >
        {{ $t('journal.all-stories') }}
      </router-link><router-link
        v-for="category in categories"
        :key="category.key"
        :to="{ path: '/blog', query: query(1, category.key) }"
        :aria-current="topic === category.key ? 'page' : undefined"
        :class="{ active: topic === category.key }"
      >
        {{ blogText(category, 'name', locale) }}
      </router-link>
    </nav>
    <p
      v-if="categoryError"
      class="journal-small-message"
      role="status"
    >
      {{ $t('journal.topics-error') }} <button
        class="product-text-button"
        @click="loadCategories"
      >
        {{ $t('general.retry') }}
      </button>
    </p>
    <section
      ref="resultsSection"
      class="journal-results"
      :aria-busy="loading"
      :aria-label="$t('journal.articles')"
    >
      <div
        v-if="loading"
        class="journal-loading"
        role="status"
      >
        <p>{{ $t('journal.loading-stories') }}</p><div
          class="journal-feature-skeleton"
          aria-hidden="true"
        /><div
          class="journal-grid"
          aria-hidden="true"
        >
          <div
            v-for="item in 3"
            :key="item"
            class="journal-card-skeleton"
          />
        </div>
      </div>
      <div
        v-else-if="error"
        class="product-message"
        role="alert"
      >
        <p>{{ $t('journal.list-error') }}</p><button @click="loadPosts">
          {{ $t('general.retry') }}
        </button>
      </div>
      <div
        v-else-if="!posts.length"
        class="product-empty journal-empty"
      >
        <GrowthIcon name="file" /><h2>{{ $t('journal.empty-title') }}</h2><p>{{ $t('journal.empty-copy') }}</p><button
          v-if="topic"
          class="growth-button"
          @click="changeTopic('')"
        >
          {{ $t('journal.all-stories') }}
        </button>
      </div>
      <template v-else>
        <BlogCard
          v-if="featured"
          :post="featured"
          :context="postContext"
          featured
        />
        <div class="journal-section-heading">
          <h2>{{ selectedCategory ? blogText(selectedCategory, 'name', locale) : $t('journal.from-journal') }}</h2><p aria-live="polite">
            {{ $t('journal.story-count', { count: n(total) }, total) }}
          </p>
        </div>
        <div
          v-if="remainingPosts.length"
          class="journal-grid"
        >
          <BlogCard
            v-for="post in remainingPosts"
            :key="post.key || post._id"
            :post="post"
            :context="postContext"
          />
        </div>
        <div class="journal-pagination">
          <p>{{ $t('journal.showing', { start: n((page - 1) * pageSize + 1), end: n((page - 1) * pageSize + posts.length), total: n(total) }) }}</p><nav
            v-if="totalPages > 1"
            :aria-label="$t('journal.pagination')"
          >
            <BasePagination
              :current-page="page"
              :page-link="pageHref"
              :total-pages="totalPages"
              :total-items="total"
              :per-page="pageSize"
              :max-visible="3"
              :show-first-last="false"
              @page-change="goToPage"
            />
          </nav>
        </div>
      </template>
    </section>
    <BlogGrowthCard />
    <AdSenseUnit
      v-if="!isPro && !loading && !error && posts.length"
      :ad-slot="AD_PLACEMENTS.blog.slot"
      ad-format="fluid"
      ad-layout-key="-4w+cd-2c-ee+178"
      container-class="journal-ad"
    />
  </div>
</template>
<script setup>
import { AD_PLACEMENTS } from '@/config/ads';
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/auth';
import { usePageSeo } from '@/composables/usePageSeo';
import { canonicalUrl } from '@/config/seoUrls';
import { SITE_URL, SITE_NAME, buildBreadcrumbSchema } from '@/config/seo';
import { blogPage, blogTopic, blogItems, blogText, blogPath, blogUrl } from '@/utils/blog';
import blogService from '@/services/blogService';
import BlogCard from '@/components/blog/BlogCard.vue';
import BlogGrowthCard from '@/components/blog/BlogGrowthCard.vue';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import BasePagination from '@/components/ui/BasePagination.vue';
import AdSenseUnit from '@/components/ui/AdSenseUnit.vue';
const { t, n, locale } = useI18n();
const route = useRoute(); const router = useRouter(); const auth = useAuthStore();
const isPro = computed(() => !!auth.currentUser?.subscription);
const page = computed(() => blogPage(route.query.page));
const topic = computed(() => blogTopic(route.query.topic));
const pageSize = 10;
const posts = ref([]); const total = ref(0); const categories = ref([]);
const loading = ref(true); const error = ref(false); const categoryError = ref(false); const resultsSection = ref(null);
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize)));
const featured = computed(() => page.value === 1 ? posts.value[0] : null);
const remainingPosts = computed(() => featured.value ? posts.value.slice(1) : posts.value);
const selectedCategory = computed(() => categories.value.find(category => category.key === topic.value));
const postContext = computed(() => ({ ...(locale.value === 'es' ? { lang: 'es' } : {}), ...(page.value > 1 ? { page: String(page.value) } : {}), ...(topic.value ? { topic: topic.value } : {}) }));
let request; let version = 0; let alive = true; let scrollAfterLoad = false;
let categoryRequest; let categoryVersion = 0;
function query(nextPage, nextTopic = topic.value) {
  const value = { ...route.query }; delete value.page; delete value.topic;
  if (nextPage > 1) value.page = String(nextPage);
  if (nextTopic) value.topic = nextTopic;
  return value;
}
function pageHref(value) { return router.resolve({ path: '/blog', query: query(value) }).href; }
function changeTopic(value) { if (topic.value !== value) router.push({ query: query(1, value) }); }
function goToPage(value) { if (loading.value || value < 1 || value > totalPages.value || value === page.value) return; scrollAfterLoad = true; router.push({ query: query(value) }); }
async function loadPosts() {
  const current = ++version; request?.abort(); request = new AbortController();
  loading.value = true; error.value = false;
  const selection = { page: page.value, topic: topic.value };
  try {
    const params = { page: selection.page, limit: pageSize, published: 1 };
    const options = { signal: request.signal };
    const response = selection.topic ? await blogService.getByCategory(selection.topic, params, options) : await blogService.getAll(params, options);
    if (!alive || current !== version) return;
    const result = blogItems(response); total.value = result.total;
    const lastPage = Math.max(1, Math.ceil(result.total / pageSize));
    if (selection.page > lastPage) { await router.replace({ query: query(lastPage, selection.topic) }); return; }
    posts.value = result.items;
  } catch { if (alive && current === version) { error.value = true; posts.value = []; } }
  finally {
    if (alive && current === version) {
      loading.value = false;
      if (scrollAfterLoad && !error.value) { scrollAfterLoad = false; await nextTick(); resultsSection.value?.scrollIntoView({ block: 'start', behavior: 'auto' }); }
    }
  }
}
async function loadCategories() {
  const current = ++categoryVersion; categoryRequest?.abort(); categoryRequest = new AbortController(); categoryError.value = false;
  try {
    const response = await blogService.getCategories({ signal: categoryRequest.signal });
    if (!Array.isArray(response?.categories)) throw new Error('Invalid categories');
    if (alive && current === categoryVersion) categories.value = response.categories.filter(category => category?.key && category.key !== 'all' && category.active !== 0);
  } catch { if (alive && current === categoryVersion) categoryError.value = true; }
}
watch(() => `${page.value}:${topic.value}`, loadPosts, { immediate: true });
loadCategories();
onBeforeUnmount(() => { alive = false; version++; categoryVersion++; request?.abort(); categoryRequest?.abort(); });
const jsonLd = computed(() => [{ '@context': 'https://schema.org', '@type': 'Blog', name: t('blog.title'), description: t('journal.description'), url: `${SITE_URL}/blog`, publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL }, blogPost: posts.value.map(post => ({ '@type': 'BlogPosting', headline: blogText(post, 'title', locale.value), description: blogText(post, 'description', locale.value), image: blogUrl(post.image) || undefined, datePublished: post.publishing_date, author: post.author?.name ? { '@type': 'Person', name: post.author.name } : undefined, url: `${SITE_URL}${blogPath(post)}` })) }, buildBreadcrumbSchema([{ name: SITE_NAME, url: '/' }, { name: t('blog.title'), url: '/blog' }])]);
const canonical = computed(() => canonicalUrl(`/blog?${new URLSearchParams(postContext.value)}`, locale.value === 'es' ? 'es' : 'en'));
const seoTitle = computed(() => {
  const label = selectedCategory.value ? `${blogText(selectedCategory.value, 'name', locale.value)} | ${t('blog.title')}` : t('blog.page_title');
  return page.value > 1 ? t('journal.seo-page', { title: label, page: page.value }) : label;
});
usePageSeo({ title: seoTitle, descriptionKey: 'blog.description', jsonLd, canonical, locales: ['en', 'es'] });
</script>
<style src="../styles/journal.css"></style>
